import React from "react";
import { LotIdentity } from "./lot_identity.jsx";
import { authorizedFetch } from "./boot.js";
import { Card, Tag } from "./ui.jsx";
import { InspectorBook } from "./inspector_book.jsx";
import { ParadeManagement } from "./parade_management.jsx";

// Inspections against the governed Collections and Actions (VD-SPEC-LAUNCH-047).
//
// The prototype in inspect.jsx kept a logged visit in the page's memory. This
// panel writes it, and reads every derived fact — whether a buyer is a repeat —
// back from the server, because the browser only ever holds a bounded page of
// the log and must not compute a count from it.

async function request(path, input, key) {
  const response = await authorizedFetch(path, input ? {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ input, idempotency_key: key }),
  } : {});
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body?.error?.code || "request_failed");
    error.status = response.status;
    throw error;
  }
  return body;
}

// Spelling-tolerant matching, kept from the prototype. It ranks a list the
// server chose; it never decides which names exist.
function editDist(a, b, cap = 3) {
  if (Math.abs(a.length - b.length) > cap) return cap + 1;
  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++)
    dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1,
      dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return dp[a.length][b.length];
}

function matchBuyers(buyers, query, limit = 6) {
  if (!query.trim()) return buyers.slice(0, limit);
  const wanted = query.trim().toLowerCase();
  const scored = [];
  for (const buyer of buyers) {
    const name = buyer.name.toLowerCase();
    let score = 0;
    if (name.startsWith(wanted)) score = 1.2;
    else if (name.includes(wanted)) score = 1.0;
    else {
      for (const token of name.split(/[\s,\-.]+/)) {
        if (token.startsWith(wanted)) score = Math.max(score, 0.9);
        else if (wanted.length >= 4
          && editDist(wanted, token.slice(0, wanted.length + 1)) <= (wanted.length > 6 ? 2 : 1)) {
          score = Math.max(score, 0.6);
        }
      }
    }
    if (score > 0) scored.push({ buyer, score: score * (1 + buyer.weight) });
  }
  return scored.sort((a, b) => b.score - a.score).slice(0, limit).map(item => item.buyer);
}

// Colour then sex, the order a catalogue page prints and a vendor reads. Either
// may be absent until the poller has filled it, so this joins what is there.
const described = (lot) => [lot.colour, lot.sex].filter(Boolean).join(" ");

const clock = (value) => {
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? "" : at.toTimeString().slice(0, 5);
};

// The form offers a wall-clock time. An inspection is written up during or just
// after the visit, so a time that lands ahead of now belongs to yesterday
// evening rather than tonight. The server still bounds what it accepts.
function instantFor(hhmm) {
  if (!/^\d{2}:\d{2}$/.test(hhmm)) return null;
  const now = new Date();
  const at = new Date(now);
  at.setHours(Number(hhmm.slice(0, 2)), Number(hhmm.slice(3, 5)), 0, 0);
  if (at.getTime() - now.getTime() > 2 * 60 * 60 * 1000) at.setDate(at.getDate() - 1);
  return at.toISOString();
}

function Typeahead({ label, value, onChange, buyers, placeholder, optional, id }) {
  const [open, setOpen] = React.useState(false);
  const [highlighted, setHighlighted] = React.useState(0);
  const input = React.useRef(null);
  const hits = React.useMemo(() => matchBuyers(buyers, value), [buyers, value]);
  const pick = (name) => { onChange(name); setOpen(false); };
  return <div className="field" style={{ position: "relative" }}>
    <label htmlFor={id}>{label} {optional && <span className="muted">(optional)</span>}</label>
    <input id={id} ref={input} value={value} placeholder={placeholder} autoComplete="off"
      onChange={event => { onChange(event.target.value); setOpen(true); setHighlighted(0); }}
      onFocus={() => setOpen(true)}
      onBlur={() => setTimeout(() => setOpen(false), 150)}
      onKeyDown={event => {
        if (!open || !hits.length) return;
        if (event.key === "ArrowDown") { event.preventDefault(); setHighlighted(index => Math.min(index + 1, hits.length - 1)); }
        if (event.key === "ArrowUp") { event.preventDefault(); setHighlighted(index => Math.max(index - 1, 0)); }
        if (event.key === "Enter") { event.preventDefault(); pick(hits[highlighted].name); }
        if (event.key === "Escape") setOpen(false);
      }}/>
    {open && hits.length > 0 && <div className="ta-menu" role="listbox">
      {hits.map((buyer, index) => <button key={buyer.name} type="button" data-buyer-option
        className={index === highlighted ? "hi" : ""}
        onMouseDown={event => {
          // preventDefault keeps the list alive until the choice lands; the
          // blur after it puts a phone's keyboard away once a name is chosen
          // (reported 24 September 2026). Enter, above, keeps the focus.
          event.preventDefault(); pick(buyer.name); input.current?.blur();
        }}>
        <span>{buyer.name}</span>
        <span className="muted" style={{ fontSize: 10.5 }}>
          {buyer.seen ? "Seen this sale" : `${buyer.visits} visit${buyer.visits === 1 ? "" : "s"}`}
          {buyer.last ? ` · last ${buyer.last}` : ""}
        </span>
      </button>)}
    </div>}
  </div>;
}

export function Inspections({ sale = "26R2R", vendor = window.VD.access.realm }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [pending, setPending] = React.useState(null);
  const [refreshRequired, setRefreshRequired] = React.useState(false);
  const [buyer, setBuyer] = React.useState("");
  const [agent, setAgent] = React.useState("");
  const [note, setNote] = React.useState("");
  const [time, setTime] = React.useState(() => new Date().toTimeString().slice(0, 5));
  const [chosen, setChosen] = React.useState([]);
  const [view, setView] = React.useState("log");
  const mounted = React.useRef(true);
  const busyRef = React.useRef(false);
  const query = new URLSearchParams({ sale, vendor });
  const recentPath = `/collections/vendor.inspections.recent@1/?${query}`;

  const load = React.useCallback(async () => {
    const recent = await request(recentPath);
    if (window.VD?.inspections?.addressBook) {
      const book = await request(`/collections/vendor.inspectors.book@1/?${new URLSearchParams({ sale })}`);
      if (book.sale !== sale || book.vendor !== vendor) throw new Error("Wrong scope");
      recent.buyers = book.inspectors.map((row, index) => ({ ...row, weight: book.inspectors.length - index }));
    }
    return recent;
  }, [recentPath]);

  React.useEffect(() => {
    mounted.current = true;
    let active = true;
    load().then((recent) => {
      if (!active) return;
      setData(recent); setLoading(false);
    }).catch(() => {
      if (!active) return;
      setError("Inspections could not be loaded. Refresh to try again.");
      setLoading(false);
    });
    return () => { active = false; mounted.current = false; };
  }, [load]);

  async function refresh() {
    if (busyRef.current) return;
    setLoading(true); setError("");
    try {
      const recent = await load();
      if (!mounted.current) return;
      setData(recent); setRefreshRequired(false);
    } catch (_) {
      if (mounted.current) setError("The current inspections could not be loaded. Refresh before logging another.");
    } finally { if (mounted.current) setLoading(false); }
  }

  async function send(attempt) {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError(""); setNotice(attempt.saving);
    try {
      await request(attempt.path, attempt.input, attempt.key);
      if (!mounted.current) return;
      setPending(null); setRefreshRequired(true);
      if (attempt.clears) { setBuyer(""); setAgent(""); setNote(""); setChosen([]); }
      try {
        const recent = await load();
        if (!mounted.current) return;
        setData(recent); setRefreshRequired(false); setNotice(attempt.said);
      } catch (_) {
        if (mounted.current) setError("It was saved, but the current inspections could not be loaded. Refresh before logging another.");
      }
    } catch (failure) {
      if (!mounted.current) return;
      setNotice("");
      if ([400, 404, 409, 412].includes(failure.status)) {
        setPending(null); setRefreshRequired(true);
        setError(failure.status === 412
          ? "This inspection changed, or the horse is withdrawn or the time is outside the accepted window. Refresh and review before saving again."
          : "This could not be accepted. Refresh and check the horses and the time.");
      } else {
        setPending(attempt);
        setError("The save could not be confirmed. Retry this same save before logging another.");
      }
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  // Recent is the log of visits this vendor typed up. A closed parade card is
  // a real visit and stays in the record — it still counts for the horse, and
  // the lot's own history shows it — but it is not something anyone here
  // wrote, and mixing the two made this list unreadable during a parade
  // (reported 22 September 2026). The board is where a card is watched.
  const inspections = (data?.inspections || []).filter(row => !row.fromParade);
  const lots = data?.lots || [];
  const placeable = lots.filter(lot => !lot.withdrawn);
  const buyers = data?.buyers || [];
  const locked = busy || loading || refreshRequired || Boolean(pending);
  const canLog = data?.canLog === true && !locked;
  const canVoid = data?.canVoid === true && !locked;
  const typed = buyer.trim().toLowerCase();
  const repeat = Boolean(typed) && buyers.some(row => row.seen && row.name.toLowerCase() === typed);
  const numbers = new Map(lots.map(lot => [lot.id, lot.number]));

  function toggle(id) {
    setChosen(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  }

  // Logging is the only write this form makes now, so the attempt shape
  // needs no path or notice parameter — both are fixed below.
  function logAttempt() {
    return {
      path: `/actions/${window.VD?.inspections?.logRef || "vendor.inspections.log@1"}/`,
      input: {
        sale, buyer: buyer.trim(), lot_ids: [...chosen],
        ...(window.VD?.inspections?.logRef === "vendor.inspections.log@2" && buyers.find(row => row.id && row.name === buyer.trim())
          ? { inspector_id: buyers.find(row => row.id && row.name === buyer.trim()).id } : {}),
        ...(agent.trim() ? { agent: agent.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(instantFor(time) ? { inspected_at: instantFor(time) } : {}),
      },
      saving: `Logging ${chosen.length === 1 ? "one horse" : `${chosen.length} horses`}…`,
      said: `Inspection logged for ${chosen.map(id => `Lot ${numbers.get(id)}`).join(", ")}.`,
      clears: true,
      key: window.crypto.randomUUID(),
    };
  }

  function log() {
    if (!canLog || !buyer.trim() || chosen.length === 0) return;
    const attempt = logAttempt();
    setPending(attempt);
    send(attempt);
  }

  // A void keeps the record: the row stays on the page, marked, and stops
  // counting. Nothing is removed, so this is never a delete confirmation.
  function voidRow(row) {
    if (!canVoid || row.voided) return;
    const attempt = {
      path: "/actions/vendor.inspections.void@1/",
      input: { sale, inspection_id: row.id, expected_revision: row.revision },
      saving: "Marking this inspection void…",
      said: "That inspection is marked void and no longer counted.",
      key: window.crypto.randomUUID(),
    };
    setPending(attempt);
    send(attempt);
  }

  // Logging a visit and the other sub-views answer different questions, and
  // one column made each of them scroll past the other. They are separate
  // views over their own reads, so switching costs nothing and refetches
  // nothing that is still on screen.
  return <>
    <div className="seg inspect-views">
      <button type="button" data-view="log" className={view === "log" ? "on" : ""}
        aria-pressed={view === "log"} onClick={() => { if (view === "book") refresh(); setView("log"); }}>Log an Inspection</button>
      {window.VD?.inspections?.parade && <button type="button" data-view="parade" className={view === "parade" ? "on" : ""}
        aria-pressed={view === "parade"} onClick={() => setView("parade")}>Parade Management</button>}
      {window.VD?.inspections?.addressBook && <button type="button" data-view="book" className={view === "book" ? "on" : ""}
        disabled={busy || Boolean(pending)} aria-pressed={view === "book"} onClick={() => setView("book")}>Address Book</button>}
    </div>
    {view === "parade" && <ParadeManagement sale={sale} vendor={vendor}/>}
    {view === "book" && <InspectorBook key={`${sale}:${vendor}`} sale={sale} vendor={vendor}/>}

    {view === "log" && <div className="inspect-grid">
    <div>
      <Card title="Log an inspection" subtitle={`${data?.vendorName || vendor} · ${sale}`}>
        <p role="status" aria-live="polite" className="muted">{notice}</p>
        {error && <p role="alert" className="error-text">{error}</p>}
        {loading && <p role="status">Loading inspections…</p>}
        {data && <>
          <Typeahead id="inspection-buyer" label="Inspector" value={buyer} onChange={setBuyer}
            buyers={buyers} placeholder="Start typing a name"/>
          {repeat && <div className="row" style={{ gap: 8, marginTop: -8, marginBottom: 12 }}>
            <Tag variant="gold">Repeat visit</Tag>
            <span className="muted" style={{ fontSize: 12 }}>Already logged for this sale.</span>
          </div>}
          <Typeahead id="inspection-agent" label="Agent / representative" optional value={agent}
            onChange={setAgent} buyers={buyers} placeholder=""/>
          <div className="row fieldrow" style={{ gap: 16 }}>
            <div className="field" style={{ flex: "0 0 130px" }}>
              <label htmlFor="inspection-time">Time</label>
              <input id="inspection-time" type="time" value={time}
                onChange={event => setTime(event.target.value)}/>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="inspection-note">Note <span className="muted">(optional)</span></label>
              <input id="inspection-note" value={note} maxLength={1000}
                onChange={event => setNote(event.target.value)}
                placeholder="Kept for your team only"/>
            </div>
          </div>
          <p className="caption">The note stays with your team and with Inglis staff working on your
            behalf. It is never part of anything sent to a horse&apos;s owner.</p>

          <h4>Which horses did they see?</h4>
          <div className="pickgrid lotpick">
            {placeable.map(lot => <button key={lot.id} type="button"
              className="horse-card pick lotpick-option"
              data-lot-option data-on={chosen.includes(lot.id) ? "1" : undefined}
              aria-pressed={chosen.includes(lot.id)} disabled={!canLog}
              onClick={() => toggle(lot.id)}>
              <LotIdentity lot={lot}/>
            </button>)}
            {!placeable.length && <p className="caption">No horse in this draft can be inspected.</p>}
          </div>
          <div className="row inspect-actions" style={{ justifyContent: "space-between", marginTop: 18 }}>
            <span className="inspect-count">
              <span className="muted inspect-selected">{chosen.length} selected</span>
              {chosen.length > 0 && <button type="button" className="btn sm" data-clear-selection
                disabled={locked} onClick={() => setChosen([])}>Clear</button>}
            </span>
            <span className="row inspect-buttons" style={{ gap: 10 }}>
              <button type="button" className="btn sm" disabled={busy || loading || Boolean(pending)}
                onClick={refresh}>Refresh</button>
              {pending && <button type="button" className="btn sm primary" disabled={busy}
                onClick={() => send(pending)}>Retry same save</button>}
              <button type="button" className="btn primary lg" data-log-inspection
                disabled={!canLog || !buyer.trim() || chosen.length === 0}
                onClick={log}>Log inspection</button>
            </span>
          </div>
          {data?.paradeEnabled && <p className="caption" data-parade-destination>
            These horses will go on the parade board
          </p>}
          {data.canLog === false && <p className="caption">Logging an inspection is not part of your access.</p>}
        </>}
      </Card>

    </div>

    <div className="inspect-side">
      <Card title="Recent" subtitle="Inspections you logged, newest first">
        {data && !inspections.length && <p className="muted">No inspection has been logged yet.</p>}
        {inspections.map((row, index) => <div key={row.id} data-inspection={row.id}
          data-voided={row.voided ? "1" : undefined}
          style={{ padding: "11px 0", borderTop: index ? "1px solid var(--divider)" : "0",
            opacity: row.voided ? 0.6 : 1 }}>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <span style={{ fontSize: 13.5, textDecoration: row.voided ? "line-through" : "none" }}>
              {row.buyer}
            </span>
            <span className="mono muted" style={{ fontSize: 11 }}>{clock(row.inspectedAt)}</span>
          </div>
          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
            {row.lots.map(lot => `Lot ${lot.number}`).join(", ")}
            {row.agent ? ` · ${row.agent}` : ""}
            {row.repeat ? " · repeat" : ""}
            {row.loggedByName ? ` · logged by ${row.loggedByName}` : ""}
          </div>
          {row.note && <div className="caption" style={{ marginTop: 2 }}>{row.note}</div>}
          {row.voided
            ? <div className="row" style={{ gap: 8, marginTop: 4 }}>
              <Tag>Voided</Tag>
              <span className="caption">Kept for the record and counted in nothing.</span>
            </div>
            : canVoid && <button type="button" className="btn sm" data-void={row.id}
              onClick={() => voidRow(row)}>Void</button>}
        </div>)}
      </Card>
    </div>
    </div>}
  </>;
}
